<template>
    <div class="container container-with-transition teacher-container">
        <transition :name="componentTransition">
            <component :is="currentPage" class="teacher-page"></component>
        </transition>
        <fullscreen-toggle-button id="fullscreen-toggle-button"/>
    </div>
</template>

<script>
    import * as extend from 'extend'

    import ConnectPage from './flow/0-connect'
    import SelectThemePage from './flow/1-select-theme'
    import SelectCasePage from './flow/2-select-case'
    import LobbyPage from './flow/3-lobby'
    import PageController from './flow/4-page-controller'

    import FullscreenToggleButton from '@/components/fullscreen-toggle-button'
    import EmojiCode from '@/components/emoji-code'
    import GameClientEvent from '@/enum/GameClientEvent'
    import GamePhase from '@/enum/GamePhase'
    import GameType from '@/enum/GameType'
    import GameClientType from '@/enum/GameClientType'
    import {ConsoleLogger} from '@/console-logger'
    import {$eBus} from '@/event-bus'
    import AppConfig from '@/app-config'
    import KickReason from "@/enum/KickReason";
    import {mapGetters} from 'vuex'

    import * as MutationTypes from '@/store/mutation-types'
    import {getTeacherUrl} from '@/utils/UrlUtil'
    import {eventActions, eventCategories, postEventData} from "@/utils/AnalyticsUtil";
    import {webSocketCodeToString} from '@/utils/WebSocketCodeUtil';

    const pageComponents = {
        'connect': ConnectPage,
        'select-theme': SelectThemePage,
        'select-case': SelectCasePage,
        'lobby': LobbyPage,
        'pages': PageController
    }

    const components = {
        EmojiCode,
        FullscreenToggleButton
    }

    export default {
        components: extend(components, pageComponents),
        data() {
            return {
                componentTransition: AppConfig.componentTransition,
                currentPage: 'connect',
                logger: new ConsoleLogger('TeacherController', 'background: #00aaaa; color: white', null, AppConfig.debug.teacherController),
                started: false,
            }
        },
        computed: {
            ...mapGetters({
                currentCase: 'getCurrentCase'
            }),
        },
        watch: {
            '$route'(to, from) {
                this.reloadCurrentPage(to)
            }
        },
        methods: {
            onRoomLeave(room, code) {
                if (!this.leftRoom) {
                    console.log('We unexpectedly left the room! Perhaps we were kicked?');
                }

                let codeString = webSocketCodeToString(code);

                console.log(`onRoomLeave - code ${code} - code string ${codeString}`);

                this.$store.dispatch('fullReset');
                this.$gameClient.disconnect();

                this.$nextTick(() => {
                    this.$router.push(getTeacherUrl('connect')); // Go back to connection screen
                });
            },
            resetGame(resetError = true, disconnect = false) {
                if (resetError) {
                    this.$store.dispatch('fullReset');
                } else {
                    this.$store.dispatch('fullResetWithoutError');
                }

                if (disconnect) {
                    this.leftRoom = true;
                    this.$gameClient.disconnect();
                }

                this.$nextTick(() => {
                    if (disconnect) {
                        this.$router.push(getTeacherUrl('connect')); // Go back to connection screen
                    } else {
                        this.$router.push(getTeacherUrl('select-theme')); // Go back to connection screen
                    }
                });
            },
            onRoomStateChanged(room, state) {
                let roomState = room && room.state ? room.state : {}

                console.log('onRoomStateChanged', room, roomState, state);

                if (roomState.hasOwnProperty('currentPageIndex') && roomState.currentPageIndex >= 0) {
                    this.$store.commit(MutationTypes.SET_CURRENT_PAGE_INDEX, roomState.currentPageIndex)
                }

                if (roomState.hasOwnProperty('pagePhase')) {
                    this.$store.commit(MutationTypes.SET_CURRENT_PAGE_PHASE, roomState.pagePhase)
                }
            },
            onPhaseChanged(newPhase) {
                this.$store.commit(MutationTypes.SET_CURRENT_GAME_PHASE, newPhase)

                if (newPhase === GamePhase.SelectTheme) {
                    this.$store.dispatch('fullReset');

                    this.$router.push(getTeacherUrl('select-theme'))
                } else if (newPhase === GamePhase.SelectCase) {
                    this.$router.push(getTeacherUrl('select-case'))
                } else if (newPhase === GamePhase.Lobby) {
                    this.logger.logMessage(`Going to Lobby as the GameClient said that's the current phase`)

                    this.$store.dispatch({type: 'reset'})
                    this.$router.push(getTeacherUrl('lobby'))
                } else if (newPhase === GamePhase.Pages) {
                    this.logger.logMessage(`Going to Pages as the GameClient said that's the current phase`)

                    this.$router.push(getTeacherUrl('pages'))

                    if (!this.started) {
                        this.onStart();
                        this.started = true;
                    }
                } else if (newPhase === GamePhase.End) {
                    if (this.started) {
                        this.onEnd();

                        this.started = false;
                    }

                    this.$store.dispatch('fullReset');

                    this.$gameClient.room.send({
                        _resetRoom: GamePhase.SelectTheme
                    });

                    this.$nextTick(() => {
                        this.resetGame(true, false);
                    });
                }
            },
            reloadCurrentPage(route) {
                let currentPage = route.params.page

                if (!currentPage || !components.hasOwnProperty(currentPage)) {
                    this.logger.logMessage(`Unknown TeacherPage "${currentPage}"`)

                    this.$router.push(getTeacherUrl('connect'))

                    return
                }

                this.logger.logMessage(`TeacherPage changed to "${currentPage}"`)

                this.currentPage = currentPage
            },
            onRoomMessage(room, message) {
                let kickMatch = message.match(/^kick:(\d+)$/);

                if (kickMatch) {
                    let kickReason = parseInt(kickMatch[1], 10);

                    if (kickReason === KickReason.TooManyRooms) {
                        console.log(`We were kicked: KickReason.TooManyRooms`);
                        this.$store.commit(MutationTypes.SET_ERROR_ICON, 'connectionError')
                        this.$store.commit(MutationTypes.SET_ERROR_MESSAGE, 'Du kan ikke åbne flere samtidige spil med din licens')
                    } else {
                        console.log(`We were kicked: KickReason.${kickReason}`);
                    }

                    this.resetGame(false, true);
                }
            },
            onStart() {
                postEventData(eventCategories.multiPlayer, eventActions.caseStart, this.currentCase);
            },
            onEnd() {
                postEventData(eventCategories.multiPlayer, eventActions.caseEnd, this.currentCase);
            },
        },
        mounted() {
            this.$store.commit(MutationTypes.SET_GAME_CLIENT_TYPE, GameClientType.Online);
            this.$store.commit(MutationTypes.SET_GAME_TYPE, GameType.ClassRoom);

            this.reloadCurrentPage(this.$router.currentRoute)

            $eBus.$off(GameClientEvent.RoomStateChange, this.onRoomStateChanged)
            $eBus.$off(GameClientEvent.PhaseChanged, this.onPhaseChanged)
            $eBus.$off(GameClientEvent.RoomMessage, this.onRoomMessage)
            $eBus.$off(GameClientEvent.RoomLeave, this.onRoomLeave)

            $eBus.$on(GameClientEvent.RoomStateChange, this.onRoomStateChanged)
            $eBus.$on(GameClientEvent.PhaseChanged, this.onPhaseChanged)
            $eBus.$on(GameClientEvent.RoomMessage, this.onRoomMessage)
            $eBus.$on(GameClientEvent.RoomLeave, this.onRoomLeave)
        },
        beforeDestroy() {
            $eBus.$off(GameClientEvent.RoomStateChange, this.onRoomStateChanged)
            $eBus.$off(GameClientEvent.PhaseChanged, this.onPhaseChanged)
            $eBus.$off(GameClientEvent.RoomMessage, this.onRoomMessage)
            $eBus.$off(GameClientEvent.RoomLeave, this.onRoomLeave)
        }
    }
</script>
<style lang="less" scoped>
    .teacher-container {
        position: relative;
    }

    .teacher-page {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
    }

    #fullscreen-toggle-button {
        position: fixed;
        top: 10px;
        right: 10px;
    }
</style>
